import { FC, useLayoutEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiLoader } from "@miplatform/components";
import {
  IAttemptListItemResponseForWorkOrder,
  LoaderType,
} from "@miplatform/types";
import Masonry from "@mui/lab/Masonry";
import { Box } from "@mui/material";
import { NHCWorkOrderCard } from "../NHCPriorityScheduler/NHCWorkOrderCard";
import { UnassignedWorkOrdersEmptyState } from "./UnassignedWorkOrdersEmptyState";

const CARD_HEIGHT = 60;
const CARD_WIDTH = 160;
const MASONRY_SPACING_PX = 4;
const LIST_PADDING_PX = 8;
const MIN_COLUMNS = 1;

const computeColumnWidth = (contentWidth: number, columns: number) => {
  if (contentWidth <= 0 || columns <= 0) return CARD_WIDTH;
  const totalSpacing = (columns - 1) * MASONRY_SPACING_PX;
  return (contentWidth - totalSpacing) / columns;
};

const computeCardWrapWidth = (contentWidth: number, columns: number) => {
  const columnWidth = computeColumnWidth(contentWidth, columns);
  const extraSpace = Math.max(0, columnWidth - CARD_WIDTH);
  return Math.floor(CARD_WIDTH + extraSpace);
};

const UnassignedWorkOrderCardItem: FC<{
  workOrder: IAttemptListItemResponseForWorkOrder;
  loading: boolean;
  cardWidth: number;

  onWorkOrderClick?: (attempt: IAttemptListItemResponseForWorkOrder) => void;
  zoom?: number;
  isBulkAssignmentMode?: boolean;
  onCheckboxChange?: (
    attempt: IAttemptListItemResponseForWorkOrder,
    isChecked: boolean
  ) => void;
  checkedAttemptIds?: ReadonlySet<string>;
  remainingDurationForUser?: number;
  remainingDurationByTemplateId?: Record<string, number>;
  hasAssignableUserColumn?: boolean;
}> = ({
  workOrder,
  loading,
  onWorkOrderClick,
  zoom = 1,
  isBulkAssignmentMode = false,
  onCheckboxChange,
  checkedAttemptIds,
  cardWidth,
  remainingDurationForUser = 0,
  remainingDurationByTemplateId,
  hasAssignableUserColumn = false,
}) => {
  const expectedDuration = workOrder.expectedDuration
    ? workOrder.expectedDuration
    : CARD_HEIGHT;

  const templateRemaining = workOrder.workOrderTemplateId
    ? remainingDurationByTemplateId?.[workOrder.workOrderTemplateId]
    : undefined;
  const remainingDuration =
    templateRemaining != null ? templateRemaining : remainingDurationForUser;
  const isAvailable =
    !hasAssignableUserColumn || remainingDuration >= expectedDuration;

  const isChecked = checkedAttemptIds?.has(workOrder.id) ?? false;
  const { classes, cx } = useStyles({
    cardHeight: zoom * expectedDuration,
    cardWidth,
    isChecked,
  });

  const handleWorkOrderClick = () => {
    if (!isAvailable) {
      return;
    }

    if (isBulkAssignmentMode) {
      const isChecked = checkedAttemptIds?.has(workOrder.id) ?? false;
      onCheckboxChange?.(workOrder, !isChecked);
      return;
    }
    onWorkOrderClick?.(workOrder);
  };

  const handleOnCheckboxChange = () => {
    if (!isAvailable && !isChecked) {
      return;
    }
    if (isBulkAssignmentMode) {
      onCheckboxChange?.(workOrder, !isChecked);
    } else {
      onWorkOrderClick?.(workOrder);
    }
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      className={cx(
        classes.cardWrap,
        !isAvailable && !isChecked && classes.cardUnavailable
      )}
      onClick={handleWorkOrderClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleOnCheckboxChange();
        }
      }}
    >
      <NHCWorkOrderCard
        isBulkAssignmentMode={isBulkAssignmentMode}
        onCheckboxChange={handleOnCheckboxChange}
        checked={isChecked}
        className={cx(classes.card, isChecked && classes.cardChecked)}
        attempt={workOrder}
        loading={loading}
      />
    </Box>
  );
};

const computeColumns = (contentWidth: number) => {
  if (contentWidth <= 0) return MIN_COLUMNS;
  const count = Math.floor(
    (contentWidth + MASONRY_SPACING_PX) / (CARD_WIDTH + MASONRY_SPACING_PX)
  );
  return Math.max(MIN_COLUMNS, count);
};

export const UnassignedWorkOrdersList: FC<UnassignedWorkOrdersListProps> = ({
  workOrders,
  loading,
  selectedTabLabel,
  onWorkOrderClick,
  zoom = 1,
  isBulkAssignmentMode = false,
  onCheckboxChange,
  checkedAttemptIds,
  remainingDurationForUser,
  remainingDurationByTemplateId,
  hasAssignableUserColumn,
}) => {
  const { classes } = useStyles({ cardHeight: zoom * CARD_HEIGHT });
  const listRef = useRef<HTMLDivElement | null>(null);
  const [columns, setColumns] = useState(MIN_COLUMNS);
  const [cardWidth, setCardWidth] = useState(CARD_WIDTH);

  useLayoutEffect(() => {
    const element = listRef.current;
    if (!element) return undefined;

    const initialContentWidth = element.clientWidth - 2 * LIST_PADDING_PX;
    const initialColumns = computeColumns(initialContentWidth);
    setColumns(initialColumns);
    setCardWidth(computeCardWrapWidth(initialContentWidth, initialColumns));

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const nextColumns = computeColumns(entry.contentRect.width);
      setColumns(nextColumns);
      setCardWidth(computeCardWrapWidth(entry.contentRect.width, nextColumns));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  if (loading) {
    return (
      <Box className={classes.loaderContainer}>
        <MiLoader isLoading type={LoaderType.Circular} />
      </Box>
    );
  }

  if (workOrders.length === 0) {
    return (
      <Box className={classes.emptyStateContainer}>
        <UnassignedWorkOrdersEmptyState selectedTabLabel={selectedTabLabel} />
      </Box>
    );
  }

  return (
    <Box ref={listRef} className={classes.list}>
      <Masonry columns={columns}>
        {workOrders.map((workOrder) => (
          <UnassignedWorkOrderCardItem
            key={workOrder.id}
            workOrder={workOrder}
            loading={loading}
            cardWidth={cardWidth}
            onWorkOrderClick={onWorkOrderClick}
            zoom={zoom}
            isBulkAssignmentMode={isBulkAssignmentMode}
            onCheckboxChange={onCheckboxChange}
            checkedAttemptIds={checkedAttemptIds}
            remainingDurationForUser={remainingDurationForUser}
            remainingDurationByTemplateId={remainingDurationByTemplateId}
            hasAssignableUserColumn={hasAssignableUserColumn}
          />
        ))}
      </Masonry>
    </Box>
  );
};

type UnassignedWorkOrdersListProps = {
  workOrders: IAttemptListItemResponseForWorkOrder[];
  loading: boolean;
  selectedTabLabel?: string | null;
  onWorkOrderClick?: (attempt: IAttemptListItemResponseForWorkOrder) => void;
  zoom?: number;
  isBulkAssignmentMode?: boolean;
  onCheckboxChange?: (
    attempt: IAttemptListItemResponseForWorkOrder,
    isChecked: boolean
  ) => void;
  checkedAttemptIds?: ReadonlySet<string>;
  remainingDurationForUser?: number;
  remainingDurationByTemplateId?: Record<string, number>;
  hasAssignableUserColumn?: boolean;
};

const useStyles = makeStyles<{
  cardHeight: number;
  cardWidth?: number;
  isChecked?: boolean;
}>()((theme, { cardHeight, cardWidth, isChecked }) => ({
  list: {
    width: "100%",
    overflow: "auto",
    padding: theme.spacing(2),
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    padding: theme.spacing(2, 0),
  },
  emptyStateContainer: {
    display: "flex",
    width: "100%",
    height: "100%",
    minHeight: 120,
  },
  cardWrap: {
    height: cardHeight,
    cursor: "pointer",
    width: cardWidth ?? CARD_WIDTH,
  },
  card: {
    width: cardWidth ?? CARD_WIDTH,
  },
  cardChecked: {
    border: `2px solid ${theme.palette.primary.main}`,
  },
  cardUnavailable: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
}));
